import React, { useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Play, TestTube, Link as LinkIcon, CheckCircle, XCircle, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdsTab = ({ vastAds, onRefresh }) => {
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newAd, setNewAd] = useState({ name: '', vast_xml_url: '', position: '' });
  const [loading, setLoading] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);

  const handleAddAd = async (e) => {
    e.preventDefault();
    if (!newAd.name.trim() || !newAd.vast_xml_url.trim() || !newAd.position) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    
    try {
      await axios.post(`${API}/admin/vast-ads`, {
        name: newAd.name.trim(),
        vast_xml_url: newAd.vast_xml_url.trim(),
        position: newAd.position
      });
      
      toast.success('VAST ad added successfully!');
      setNewAd({ name: '', vast_xml_url: '', position: '' });
      setShowAddDialog(false);
      setTestResult(null);
      onRefresh();
    } catch (error) {
      console.error('Error adding VAST ad:', error);
      toast.error(error.response?.data?.detail || 'Failed to add VAST ad');
    } finally {
      setLoading(false);
    }
  };

  const handleTestVast = async () => {
    if (!newAd.vast_xml_url.trim()) {
      toast.error('Please enter a VAST XML URL first');
      return;
    }

    setTesting(true);
    setTestResult(null);
    
    try {
      const response = await axios.post(`${API}/admin/vast-ads/test`, {
        url: newAd.vast_xml_url.trim()
      });
      
      setTestResult(response.data);
      if (response.data.status === 'success') {
        toast.success('VAST XML is valid!');
      } else {
        toast.error('VAST XML validation failed');
      }
    } catch (error) {
      console.error('Error testing VAST XML:', error);
      setTestResult({
        status: 'error',
        message: error.response?.data?.detail || 'Failed to test VAST XML',
        ads: []
      });
      toast.error('Failed to test VAST XML');
    } finally {
      setTesting(false);
    }
  };

  const handleDeleteAd = async (adId) => {
    if (!window.confirm('Are you sure you want to delete this VAST ad?')) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/vast-ads/${adId}`);
      toast.success('VAST ad deleted successfully');
      onRefresh();
    } catch (error) {
      console.error('Error deleting VAST ad:', error);
      toast.error('Failed to delete VAST ad');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-3xl font-brand font-bold text-white">VAST Ad Management</h2>
        
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogTrigger asChild>
            <Button className="btn-hover bg-gradient-to-r from-purple-600 to-pink-600 text-white" data-testid="add-vast-ad-btn">
              <Plus className="h-4 w-4 mr-2" />
              Add VAST Ad
            </Button>
          </DialogTrigger>
          
          <DialogContent className="glass-dark border-white/20 text-white max-w-2xl">
            <DialogHeader>
              <DialogTitle className="text-white">Add New VAST Ad</DialogTitle>
            </DialogHeader>
            
            <form onSubmit={handleAddAd} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="ad-name" className="text-white">Ad Name</Label>
                <Input
                  id="ad-name"
                  type="text"
                  value={newAd.name}
                  onChange={(e) => setNewAd(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Enter ad name (e.g., Google AdX Pre-roll)"
                  className="form-input text-white"
                  required
                  data-testid="ad-name-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="vast-url" className="text-white">VAST XML URL</Label>
                <div className="flex space-x-2">
                  <div className="relative flex-1">
                    <LinkIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-white/50" />
                    <Input
                      id="vast-url"
                      type="url"
                      value={newAd.vast_xml_url}
                      onChange={(e) => setNewAd(prev => ({ ...prev, vast_xml_url: e.target.value }))}
                      placeholder="https://example.com/vast.xml"
                      className="pl-10 form-input text-white"
                      required
                      data-testid="vast-url-input"
                    />
                  </div>
                  <Button
                    type="button"
                    onClick={handleTestVast}
                    disabled={testing}
                    className="btn-hover bg-blue-600 hover:bg-blue-700 text-white"
                    data-testid="test-vast-btn"
                  >
                    {testing ? (
                      <div className="flex items-center">
                        <div className="loading-spinner mr-2"></div>
                        Testing
                      </div>
                    ) : (
                      <div className="flex items-center">
                        <TestTube className="h-4 w-4 mr-2" />
                        Test
                      </div>
                    )}
                  </Button>
                </div>
                <p className="text-xs text-white/60">
                  Enter your VAST XML URL from ad networks like Google AdX, SpotX, or other providers
                </p>
              </div>
              
              {/* Test Results */}
              {testResult && (
                <div className="space-y-2">
                  <Alert className={`border-${testResult.status === 'success' ? 'green' : 'red'}-500/50 bg-${testResult.status === 'success' ? 'green' : 'red'}-500/10`}>
                    {testResult.status === 'success' ? (
                      <CheckCircle className="h-4 w-4" />
                    ) : (
                      <XCircle className="h-4 w-4" />
                    )}
                    <AlertDescription className={`text-${testResult.status === 'success' ? 'green' : 'red'}-400`}>
                      {testResult.status === 'success' ? (
                        <div>
                          <strong>VAST XML Valid!</strong> Found {testResult.ads.length} ad(s).
                          {testResult.ads.length > 0 && (
                            <div className="mt-2 text-xs">
                              <strong>First Ad:</strong> {testResult.ads[0].media_url ? 'Media found' : 'No media'} | 
                              Duration: {testResult.ads[0].duration || 'Unknown'}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div><strong>Validation Failed:</strong> {testResult.message}</div>
                      )}
                    </AlertDescription>
                  </Alert>
                </div>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="ad-position" className="text-white">Ad Position</Label>
                <Select value={newAd.position} onValueChange={(value) => setNewAd(prev => ({ ...prev, position: value }))}>
                  <SelectTrigger className="form-input text-white" data-testid="ad-position-select">
                    <SelectValue placeholder="Select ad position" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-white/20">
                    <SelectItem value="pre-roll" className="text-white hover:bg-white/10">
                      Pre-roll (Before video)
                    </SelectItem>
                    <SelectItem value="mid-roll" className="text-white hover:bg-white/10">
                      Mid-roll (During video)
                    </SelectItem>
                    <SelectItem value="post-roll" className="text-white hover:bg-white/10">
                      Post-roll (After video)
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex space-x-3">
                <Button
                  type="submit"
                  disabled={loading}
                  className="flex-1 btn-hover bg-gradient-to-r from-purple-600 to-pink-600 text-white"
                  data-testid="submit-vast-ad"
                >
                  {loading ? (
                    <div className="flex items-center">
                      <div className="loading-spinner mr-2"></div>
                      Adding...
                    </div>
                  ) : (
                    'Add VAST Ad'
                  )}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowAddDialog(false);
                    setTestResult(null);
                  }}
                  className="border-white/20 text-white hover:bg-white/10"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
      
      {/* VAST Ads List */}
      <div className="space-y-4">
        {vastAds.length === 0 ? (
          <Card className="glass-dark border-white/20">
            <CardContent className="p-12 text-center">
              <div className="text-6xl mb-4">📺</div>
              <h3 className="text-xl font-semibold mb-2 text-white">No VAST Ads Yet</h3>
              <p className="text-white/70 mb-4">
                Add VAST ads to monetize your videos with pre-roll, mid-roll, and post-roll advertisements.
              </p>
              <Button
                onClick={() => setShowAddDialog(true)}
                className="btn-hover bg-gradient-to-r from-purple-600 to-pink-600 text-white"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Your First VAST Ad
              </Button>
            </CardContent>
          </Card>
        ) : (
          vastAds.map((ad) => (
            <VastAdItem key={ad.id} ad={ad} onDelete={() => handleDeleteAd(ad.id)} />
          ))
        )}
      </div>
    </div>
  );
};

const VastAdItem = ({ ad, onDelete }) => {
  const getPositionColor = (position) => {
    switch (position) {
      case 'pre-roll': return 'from-green-500 to-blue-500';
      case 'mid-roll': return 'from-yellow-500 to-orange-500';
      case 'post-roll': return 'from-purple-500 to-pink-500';
      default: return 'from-gray-500 to-gray-600';
    }
  };

  const getPositionIcon = (position) => {
    return <Play className="h-4 w-4" />;
  };

  return (
    <Card className="glass-dark border-white/20 card-hover">
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0 mr-6">
            <div className="flex items-center space-x-3 mb-3">
              <div className={`p-2 bg-gradient-to-r ${getPositionColor(ad.position)} rounded-lg`}>
                {getPositionIcon(ad.position)}
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">{ad.name}</h3>
                <div className="flex items-center space-x-3">
                  <Badge className={`bg-gradient-to-r ${getPositionColor(ad.position)} text-white border-none`}>
                    {ad.position.charAt(0).toUpperCase() + ad.position.slice(1).replace('-', ' ')}
                  </Badge>
                  <Badge 
                    variant={ad.is_active ? 'default' : 'secondary'} 
                    className={ad.is_active ? 'bg-green-500/20 text-green-300 border-green-500/30' : 'bg-gray-500/20 text-gray-400'}
                  >
                    {ad.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
              </div>
            </div>
            
            <div className="space-y-2">
              <div>
                <span className="text-sm text-white/70">VAST XML URL:</span>
                <a 
                  href={ad.vast_xml_url} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="block text-sm text-purple-400 hover:text-purple-300 truncate mt-1"
                >
                  {ad.vast_xml_url}
                </a>
              </div>
              
              <div className="flex items-center space-x-4 text-xs text-white/60">
                <span>Created: {new Date(ad.created_at).toLocaleDateString()}</span>
              </div>
            </div>
          </div>
          
          {/* Delete Button */}
          <div className="flex items-center space-x-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={onDelete}
              className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
              data-testid={`delete-vast-ad-${ad.id}`}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default AdsTab;